import React, {useEffect, useRef, useState} from 'react';
import {Button} from "primereact/button";
import {ProgressBar} from "primereact/progressbar";

import {LanguageService} from "../../service/LanguageService";
import {UnAuthService} from "../../service/UnAuthService";
import {withOpenAIAttribution} from "../../utils/openaiAttribution";

const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const linkedInClientId = process.env.REACT_APP_LINKEDIN_CLIENT_ID;
const linkedInScope = process.env.REACT_APP_LINKEDIN_SCOPE || "openid profile email";
const linkedInAuthorizeUrl = process.env.REACT_APP_LINKEDIN_AUTHORIZE_URL || "https://www.linkedin.com/oauth/v2/authorization";

const getBrowserTimezone = () => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
};

const base64UrlEncode = (arrayBuffer) => {
    return btoa(String.fromCharCode.apply(null, new Uint8Array(arrayBuffer)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
};

const generateRandomString = (length) => {
    const charset = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
    const randomValues = new Uint8Array(length);
    window.crypto.getRandomValues(randomValues);

    return Array.from(randomValues).map(value => charset[value % charset.length]).join('');
};

const createPkceChallenge = async (verifier) => {
    const data = new TextEncoder().encode(verifier);
    const digest = await window.crypto.subtle.digest('SHA-256', data);
    return base64UrlEncode(digest);
};

// `mode` picks the wording only: "signup" on the register screen, "login"
// everywhere else. The OAuth flow behind both is identical — Google and
// LinkedIn create the account on first use — but a button that reads "Sign in"
// tells a visitor without an account that it is not meant for them.
const SocialLoginButtons = ({t, messages, setError, onSuccess, disabled, mode = "login"}) => {

    const [socialLoading, setSocialLoading] = useState({google: false, linkedin: false});
    const googleButton = useRef(null);
    const isSignup = mode === "signup";

    const isBusy = disabled || socialLoading.google || socialLoading.linkedin;

    // First use of a social login creates the trial account, so it carries
    // the OpenAI ad reference too; the backend ignores it for existing users.
    const socialPayloadBase = () => {
        return withOpenAIAttribution({
            language: LanguageService.getLanguage() || "en",
            timezone: getBrowserTimezone()
        });
    };

    const showMessage = (detail) => {
        if (messages && messages.current) {
            messages.current.show({sticky: true, severity: "error", summary: "", detail: detail});
        }
    };

    const showSocialError = (error) => {
        if (error && error.response && error.response.status === 401) {
            showMessage(t('login.social_verify_failed'));
            return;
        }

        setError(error);
        setError(null);
    };

    const googleLogin = (response) => {
        if (isBusy || !response || !response.credential) {
            return;
        }

        setSocialLoading(prevState => ({...prevState, google: true}));

        UnAuthService.socialLogin("google", {
            id_token: response.credential,
            ...socialPayloadBase()
        }).then(() => {
            onSuccess();
        }).catch(error => {
            showSocialError(error);
        }).finally(() => {
            setSocialLoading(prevState => ({...prevState, google: false}));
        });
    };

    useEffect(() => {
        if (!googleClientId || !googleButton.current) {
            return;
        }

        const renderGoogleButton = () => {
            window.google.accounts.id.initialize({
                client_id: googleClientId,
                callback: googleLogin,
                ux_mode: "popup"
            });
            window.google.accounts.id.renderButton(googleButton.current, {
                theme: "outline",
                size: "large",
                text: isSignup ? "signup_with" : "signin_with",
                shape: "rectangular",
                width: googleButton.current.offsetWidth || 360,
                locale: LanguageService.getLanguage()
            });
        };

        if (window.google && window.google.accounts && window.google.accounts.id) {
            renderGoogleButton();
            return;
        }

        const scriptId = "google-identity-services";
        let script = document.getElementById(scriptId);

        if (!script) {
            script = document.createElement("script");
            script.id = scriptId;
            script.src = "https://accounts.google.com/gsi/client";
            script.async = true;
            script.defer = true;
            document.body.appendChild(script);
        }

        script.addEventListener("load", renderGoogleButton);

        return () => {
            script.removeEventListener("load", renderGoogleButton);
        };
    }, [])

    const requestLinkedInCode = async () => {
        const width = 560;
        const height = 700;
        const left = window.screenX + ((window.outerWidth - width) / 2);
        const top = window.screenY + ((window.outerHeight - height) / 2);
        const state = generateRandomString(32);
        const codeVerifier = generateRandomString(96);
        const codeChallenge = await createPkceChallenge(codeVerifier);
        const redirectUri = `${window.location.origin}?linkedin_oauth=1`;
        const params = new URLSearchParams({
            response_type: "code",
            client_id: linkedInClientId,
            redirect_uri: redirectUri,
            scope: linkedInScope,
            state: state,
            code_challenge: codeChallenge,
            code_challenge_method: "S256"
        });
        const popup = window.open(`${linkedInAuthorizeUrl}?${params.toString()}`, "linkedin-login", `width=${width},height=${height},left=${left},top=${top}`);

        if (!popup) {
            throw new Error("LinkedIn popup blocked");
        }

        return new Promise((resolve, reject) => {
            const timeout = window.setTimeout(() => {
                window.removeEventListener("message", onMessage);
                reject(new Error("LinkedIn login timed out"));
            }, 120000);

            const onMessage = (event) => {
                if (event.origin !== window.location.origin || !event.data || event.data.type !== "linkedin_oauth_callback") {
                    return;
                }

                window.clearTimeout(timeout);
                window.removeEventListener("message", onMessage);

                if (event.data.error) {
                    reject(new Error(event.data.errorDescription || event.data.error));
                    return;
                }

                if (event.data.state !== state || !event.data.code) {
                    reject(new Error("LinkedIn login state mismatch"));
                    return;
                }

                resolve({code: event.data.code, codeVerifier, redirectUri});
            };

            window.addEventListener("message", onMessage);
        });
    };

    const linkedInLogin = async () => {
        if (isBusy) {
            return;
        }

        if (!linkedInClientId) {
            showMessage(t('login.linkedin_config_missing'));
            return;
        }

        setSocialLoading(prevState => ({...prevState, linkedin: true}));

        try {
            const authCode = await requestLinkedInCode();

            await UnAuthService.socialLogin("linkedin", {
                authorization_code: authCode.code,
                redirect_uri: authCode.redirectUri,
                code_verifier: authCode.codeVerifier,
                ...socialPayloadBase()
            });
            onSuccess();
        }
        catch (error) {
            showSocialError(error);
        }
        finally {
            setSocialLoading(prevState => ({...prevState, linkedin: false}));
        }
    };

    const showGoogleConfigMissing = () => {
        showMessage(t('login.google_config_missing'));
    };

    return <>
        <div className="social-login-actions">
            <div className={socialLoading.google || isBusy ? "google-login-wrapper social-login-disabled" : "google-login-wrapper"}>
                {googleClientId ?
                    <div ref={googleButton}></div> :
                    <Button label={t(isSignup ? 'login.signup_google' : 'login.login_google')} className="google-login-fallback" disabled={isBusy} onClick={showGoogleConfigMissing}/>
                }
                {socialLoading.google && <ProgressBar mode="indeterminate" style={{height: '3px'}} />}
            </div>
            <Button type="button" className="linkedin-login-button" disabled={isBusy} onClick={linkedInLogin}>
                <span className="linkedin-login-icon">in</span>
                <span>{socialLoading.linkedin ?
                    t(isSignup ? 'login.signup_loading' : 'login.social_loading') :
                    t(isSignup ? 'login.signup_linkedin' : 'login.login_linkedin')}</span>
            </Button>
            {socialLoading.linkedin && <ProgressBar mode="indeterminate" style={{height: '3px'}} />}
        </div>
    </>;
};

export default SocialLoginButtons;
